import { useCallback } from "react"
import { useSelector } from "react-redux"
import { useToaster } from "@hellocontento/maillard"

import { callApi } from "utils/ContentoApi"
import type { GenerationOptions, SourceInput } from "./types"

// Browsers don't always set File.type (notably for .md, sometimes .csv), so we
// fall back to looking up the MIME type from the extension. Must stay aligned
// with ACCEPTED_CONTENT_TYPES in scheduler/src/services/files/upload.ts.
const MIME_TYPE_BY_EXTENSION: Record<string, string> = {
  ".pdf": "application/pdf",
  ".pptx": "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".csv": "text/csv",
  ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".txt": "text/plain",
  ".md": "text/markdown",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".png": "image/png",
  ".gif": "image/gif",
  ".webp": "image/webp",
  ".zip": "application/zip",
  ".mp3": "audio/mpeg",
}

const resolveContentType = (file: File): string => {
  if (file.type) return file.type
  const extension = file.name.toLowerCase().slice(file.name.lastIndexOf("."))
  return MIME_TYPE_BY_EXTENSION[extension] ?? ""
}

interface RootState {
  auth: { currentUser: { id: string } }
}

// Owns the create-generation calls (batch + profile) shared by the search page and
// the result tabs: source resolution, file upload, the POST, and error toasts.
// Returns the new id on success and null on failure (after toasting).
export const useCreateGeneration = (accountId: string) => {
  const addToast = useToaster()
  const currentUser = useSelector((state: RootState) => state.auth.currentUser)

  const uploadFileAndGetKey = useCallback(
    async (file: File): Promise<string> => {
      const contentType = resolveContentType(file)
      const { signedUrl, fileKey } = await callApi({
        method: "post",
        url: `content-factory/accounts/${accountId}/uploads/signed-url`,
        data: { fileName: file.name, contentType },
      }, true, true)

      const uploadResponse = await fetch(signedUrl, {
        method: "PUT",
        headers: { "Content-Type": contentType },
        body: file,
      })
      if (!uploadResponse.ok) throw new Error("Upload failed")

      return fileKey
    },
    [accountId],
  )

  const postGeneration = useCallback(
    async (endpoint: string, source: SourceInput, errorMessage: string, options: GenerationOptions = {}) => {
      const { profileId, channelSelections = [], planning, customInstructions } = options
      try {
        const sourceData = source.type === "url"
          ? { url: source.url }
          : { fileKey: await uploadFileAndGetKey(source.file), fileName: source.file.name }
        return await callApi({
          method: "post",
          url: `content-factory/accounts/${accountId}/${endpoint}`,
          data: { ...sourceData, profileId, channelSelections, userId: currentUser.id, ...planning, customInstructions },
        }, true, true)
      } catch (err: any) {
        const status = err?.status
        const message = err?.data?.message || err?.data?.error || err?.message
        addToast(status && status < 500 && message ? message : errorMessage, "error")
        return null
      }
    },
    [accountId, currentUser.id, addToast, uploadFileAndGetKey],
  )

  const createBatch = useCallback(
    async (source: SourceInput, options: GenerationOptions = {}): Promise<string | null> => {
      const response = await postGeneration("postBatches", source, "There was an error generating posts.", options)
      return response?.postBatchId ?? null
    },
    [postGeneration],
  )

  const createProfile = useCallback(
    async (source: SourceInput): Promise<string | null> => {
      const response = await postGeneration("profiles", source, "There was an error creating the profile.")
      return response?.profileId ?? null
    },
    [postGeneration],
  )

  return { createBatch, createProfile }
}
